import { useQuery } from '@tanstack/react-query'
import { Outlet, createFileRoute } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import { LinkButton } from '~/features/navigation'
import { ScorecardMutators } from '~/mutators/scorecards'
import { useTRPC } from '~/services/trpc'

import {
  AlertDialog,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Heading1,
  MagicText,
} from '@hap/components'

import { DuplicateScorecardForm } from '../../settings/scorecards/-components/scorecard-forms'

export const Route = createFileRoute(
  '/_authenticated/$alias/_app/scorecard/$scorecardId',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const params = Route.useParams()
  const trpc = useTRPC()
  const { data: scorecard } = useQuery(
    trpc.scorecards.getById.queryOptions(params.scorecardId),
  )
  const alias = params.alias

  const { update } = ScorecardMutators.useUpdate()

  // Dialog state
  const [duplicateVisibility, setDuplicateVisibility] = useState(false)
  const [deleteVisibility, setDeleteVisibility] = useState(false)

  const { destroy } = ScorecardMutators.useDestroy({
    onSuccess: () => {
      navigate({ to: '/$alias/scorecards', params: { alias } })
    },
  })

  const handleDelete = useCallback(async () => {
    await destroy({ scorecardId: params.scorecardId })
  }, [params.scorecardId, destroy])

  return (
    <div>
      <header className="space-between flex items-center">
        <Heading1 className="shirnk grow p-4">
          <MagicText
            initialValue={scorecard?.name}
            onCommit={(value) =>
              update({ scorecardId: params.scorecardId, values: { name: value } })
            }
          />
        </Heading1>
        <div className="flex items-center gap-2">
          <div>
            <LinkButton
              variant="secondary"
              to="/$alias/scorecard/$scorecardId/edit"
              from={Route.fullPath}
            >
              Configure
            </LinkButton>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" icon="dots-three-outline" size="icon" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setDuplicateVisibility(true)} icon="copy">
                Duplicate...
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setDeleteVisibility(true)}
                icon="trash"
                variant="destructive"
              >
                Delete...
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="border-b p-1">
        <nav className="flex items-center gap-2">
          <LinkButton
            variant="ghost"
            to="/$alias/scorecard/$scorecardId"
            from={Route.fullPath}
          >
            Stats
          </LinkButton>

          <Button variant="ghost">Team Feedback (0)</Button>
        </nav>
      </div>
      <Outlet />

      <DuplicateScorecardForm
        open={duplicateVisibility}
        onOpenChange={setDuplicateVisibility}
        scorecardId={params.scorecardId}
        scorecardName={scorecard?.name ?? ''}
        onSuccess={(newScorecardId: string) => {
          navigate({
            to: '/$alias/scorecard/$scorecardId',
            params: { alias, scorecardId: newScorecardId },
          })
        }}
      />

      <AlertDialog
        open={deleteVisibility}
        onOpenChange={setDeleteVisibility}
        title="Delete scorecard"
        description="Confirm that you wish to delete this scorecard. This cannot be undone."
        action={{
          onClick: handleDelete,
          value: 'Delete',
          variant: 'destructive',
        }}
      />
    </div>
  )
}
